import { useState, useEffect, useCallback } from 'react'
import { api } from '../../api/client'

// Real backing store for the Lead detail modal's "Schedule Meeting"
// (Appointments) widget - see packages/db/src/goodday/LeadMeeting.ts for why
// this replaced a localStorage-only mock with free-text, unnotified
// "attendees". Scheduling a meeting here actually notifies every invited
// real user server-side (POST /leads/:leadId/meetings).

export interface LeadMeetingPerson {
  id: string
  name: string
  email?: string
  avatarUrl?: string
}

export interface LeadMeeting {
  id: string
  title: string
  scheduledAt: string
  durationMinutes: number
  type: 'Google Meet' | 'Zoom' | 'Phone Call' | 'In-Person'
  host: LeadMeetingPerson | null
  attendees: LeadMeetingPerson[]
  meetingUrl?: string
  status: 'Confirmed' | 'Tentative' | 'Completed' | 'Cancelled'
  agenda?: string
  // Set by the API: host or creator is a Super Admin, so non-Super-Admins get
  // a view-only copy (edit/delete hidden; the server rejects them anyway).
  isSuperAdminOwned?: boolean
}

export interface CreateLeadMeetingInput {
  title: string
  scheduledAt: string
  durationMinutes?: number
  type?: LeadMeeting['type']
  hostUserId: string
  attendeeUserIds: string[]
  meetingUrl?: string
  agenda?: string
}

export interface UpdateLeadMeetingInput {
  title?: string
  scheduledAt?: string
  durationMinutes?: number
  type?: LeadMeeting['type']
  hostUserId?: string
  attendeeUserIds?: string[]
  meetingUrl?: string
  agenda?: string
  status?: LeadMeeting['status']
}

function toPerson(val: any): LeadMeetingPerson | null {
  if (!val) return null
  const id = val._id ?? val.id ?? val
  if (typeof val !== 'object') return { id: String(val), name: 'Unknown' }
  return { id: String(id), name: val.name || 'Unknown', email: val.email, avatarUrl: val.avatarUrl }
}

function fromApiRow(row: any): LeadMeeting {
  return {
    id: row._id ?? row.id,
    title: row.title,
    scheduledAt: row.scheduledAt,
    durationMinutes: row.durationMinutes ?? 30,
    type: row.type ?? 'Google Meet',
    host: toPerson(row.hostUserId),
    attendees: (row.attendeeUserIds ?? []).map(toPerson).filter(Boolean) as LeadMeetingPerson[],
    meetingUrl: row.meetingUrl,
    status: row.status ?? 'Confirmed',
    agenda: row.agenda,
    isSuperAdminOwned: Boolean(row.isSuperAdminOwned),
  }
}

export function useLeadMeetings(leadId: string | undefined) {
  const [meetings, setMeetings] = useState<LeadMeeting[]>([])
  const [loading, setLoading] = useState(true)

  const fetchMeetings = useCallback(async () => {
    if (!leadId) {
      setMeetings([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(`/leads/${leadId}/meetings`)
      const rows = res?.data || []
      setMeetings(rows.map(fromApiRow))
    } catch (err) {
      console.error('Failed to load meetings', err)
      setMeetings([])
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchMeetings()
  }, [fetchMeetings])

  const createMeeting = useCallback(
    async (input: CreateLeadMeetingInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.post<any>(`/leads/${leadId}/meetings`, input, { skipErrorToast: true })
      const created = fromApiRow(res?.data)
      setMeetings((prev) => [...prev, created].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)))
      return created
    },
    [leadId]
  )

  const updateMeeting = useCallback(
    async (id: string, input: UpdateLeadMeetingInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.patch<any>(`/leads/${leadId}/meetings/${id}`, input, { skipErrorToast: true })
      const updated = fromApiRow(res?.data)
      setMeetings((prev) => prev.map((m) => (m.id === id ? updated : m)).sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)))
      return updated
    },
    [leadId]
  )

  const deleteMeeting = useCallback(
    async (id: string) => {
      if (!leadId) throw new Error('leadId is required')
      await api.delete<any>(`/leads/${leadId}/meetings/${id}`, { skipErrorToast: true })
      setMeetings((prev) => prev.filter((m) => m.id !== id))
    },
    [leadId]
  )

  return { meetings, loading, createMeeting, updateMeeting, deleteMeeting, refetch: fetchMeetings }
}
